import React, { useEffect, useMemo, useState } from "react";
import TagsHeader from "./TagsHeader";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { CloseRounded } from "@mui/icons-material";
import { v4 as uuid } from "uuid";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import DataTableLayout from "../Common/UI/DataTableLayout";
import DataTableFooter from "../Common/UI/DataTableFooter";
import {
  dataTableListShell,
  dataTablePageShell,
} from "../../utils/dataTableShell";
import { debounce } from "lodash";
import { MoreVert } from "@mui/icons-material";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import http from "../../utils/http";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import CreateTagModal from "./CreateTagModal";
import { AxiosResponse } from "axios";
import StatusBadge from "../Common/UI/StatusTag";

const statusMap: any = {
  active: { label: "Active", variant: "active" },
  inactive: { label: "Inactive", variant: "completed" },
};
const GlobalTags = () => {
  const [searchText, setSearchText] = useState("");
  const [searchId, setSearchId] = useState<string>(uuid());
  const [loading, setLoading] = useState(false);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [data, setData] = useState<any[]>([]);
  // const [dataCount, setDataCount] = useState(0);
  // const [page, setPage] = useState(0);
  const { id: studyId } = useParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [menuLoader, setMenuLoader] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setLoading(true);
        let url = `/platform-tags`;
        if (searchText) {
          url += `?query=${searchText}`;
        }
        const res: AxiosResponse = await http.get(url);
        const newData = res?.data?.data?.map((item: any) => ({
          id: item?.id,
          name: item?.name,
          status: item?.status,
          isEditable: item?.isEditable,
          date: item?.createdAt
            ? DateTime.fromISO(item?.createdAt).toFormat("MM/dd/yyyy")
            : "",
        }));
        // const count = newData.length;
        setData(newData);
        // setDataCount(count);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchTags();
  }, [studyId, toggleLoader, searchText]);

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        // setPage(0);
        setSearchText(val);
      }, 500),
    [setSearchText]
  );

  const handleClearSearch = () => {
    setSearchText("");
    setSearchId(uuid());
  };

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  // const handleChangePage = (_1: any, newPage: number) => {
  //   setPage(newPage);
  // };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openModal = () => {
    setShowModal(true);
    handleMenuClose();
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const handleActionClick = async () => {
    if (!selectedRow) return;
    try {
      setMenuLoader(true);
      const res: AxiosResponse = await http.patch(
        `/platform-tags/${selectedRow?.id}/status`,
        {
          status: selectedRow?.status === "active" ? "inactive" : "active",
        }
      );
      toastMessage("success", res.data.message);
      refreshPage();
      handleMenuClose();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setMenuLoader(false);
    }
  };

  return (
    <>
      <TagsHeader refreshPage={refreshPage} />
      <Box sx={{ p: 2.5, ...dataTablePageShell("calc(100% - 72px)") }}>
        <Box
          sx={{
            ...dataTableListShell,
            p: 2,
            backgroundColor: "#fff",
            borderRadius: "10px",
          }}
        >
          <DataTableLayout
            toolbar={
              <TextField
                key={searchId}
                placeholder="Search for Tags"
                style={{ width: "30%" }}
                defaultValue={searchText}
                onChange={(e) => modifySearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch}>
                        <CloseRounded />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            }
            footer={
              <DataTableFooter
                filteredCount={data.length}
                page={0}
                pageSize={pageSize}
                onPageChange={() => {}}
                loading={loading}
                hidePagination
              />
            }
          >
            <Table
              sx={TableBorderRadiusTopLeftRight}
              stickyHeader
              className="scrollable-table"
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>Tag Name</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Creation Date</StyledTableCell>
                  <StyledTableCell></StyledTableCell>
                </TableRow>
              </TableHead>
              {!loading && data?.length > 0 && (
                <TableBody>
                  {data?.map((row: any) => (
                    <TableRow key={row?.id}>
                      <StyledTableCell>{row?.name || "-"}</StyledTableCell>
                      <StyledTableCell
                        sx={{
                          textTransform: "capitalize",
                        }}
                      >
                        <StatusBadge
                          variant={statusMap[row?.status]?.variant}
                          label={statusMap[row?.status]?.label ?? row?.status}
                        />
                      </StyledTableCell>
                      <StyledTableCell>{row?.date || "-"}</StyledTableCell>

                      <StyledTableCell align="right">
                        <IconButton onClick={(e) => handleMenuClick(e, row)}>
                          <MoreVert />
                        </IconButton>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {!loading && data.length === 0 && (
              <NoDataContainer>
                <Typography variant="body1" color="gray">
                  No Data
                </Typography>
              </NoDataContainer>
            )}
            {loading && (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            )}
          </DataTableLayout>
          <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  maxHeight: "400px",
                  width: "20ch",
                },
              }}
              TransitionProps={{
                exit: false,
              }}
            >
              <MenuItem onClick={openModal} disabled={!selectedRow?.isEditable}>
                <ListItemText>Edit Tag</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleActionClick} disabled={menuLoader || !selectedRow?.isEditable}>
                <ListItemText>
                  {selectedRow?.status === "active" ? "Disable" : "Enable"}
                </ListItemText>
                {menuLoader && (
                  <ListItemIcon>
                    <CircularProgress size={18} sx={{ ml: 1 }} />
                  </ListItemIcon>
                )}
              </MenuItem>
            </Menu>
        </Box>
      </Box>
      {showModal && (
        <CreateTagModal
          data={selectedRow}
          open={showModal}
          closeModal={closeModal}
          refreshPage={refreshPage}
        />
      )}
    </>
  );
};

export default GlobalTags;
